var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ADD_CONVERSATION from 'api/mutations/ADD_CONVERSATION';
import ADD_CONVERSATION_MEMBER from 'api/mutations/ADD_CONVERSATION_MEMBER';
import ADD_CONVERSATION_MESSAGE from 'api/mutations/ADD_CONVERSATION_MESSAGE';
import REMOVE_CONVERSATION_MEMBER from 'api/mutations/REMOVE_CONVERSATION_MEMBER';
import AUTOCOMPLETE_USERS from 'api/queries/AUTOCOMPLETE_USERS';
import GET_CONVERSATION from 'api/queries/GET_CONVERSATION';
import { isReadOnlyError } from 'api/readOnlyAccess';
import withAttachments from 'utils/graphql';
import client from '../api/environment';
const initialState = {
    id: null,
    title: null,
    messages: [],
    followers: [],
    members: [],
    opened: false,
    screenshotOpened: false,
    status: {
        fetch: 'idle',
        fetchMembers: 'idle',
        addChat: 'idle',
        addMessage: 'idle',
        addMember: 'idle',
        removeMember: 'idle',
    },
    errors: {
        fetch: null,
        fetchMembers: null,
        addChat: null,
        addMessage: null,
        addMember: null,
        removeMember: null,
    },
    refetchFlags: {
        fetch: false,
        fetchMembers: false,
        addChat: false,
        addMessage: false,
        addMember: false,
        removeMember: false,
    },
};
export const fetchChat = createAsyncThunk('chat/fetchChat', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ url }, { rejectWithValue }) {
    var _c, _d;
    try {
        const chatData = yield client.query({
            query: GET_CONVERSATION,
            variables: { url },
            fetchPolicy: 'network-only',
        });
        return (_d = (_c = chatData.data) === null || _c === void 0 ? void 0 : _c.me) === null || _d === void 0 ? void 0 : _d.conversation;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchMembers = createAsyncThunk('chat/fetchMembers', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const membersData = yield client.query({
            query: AUTOCOMPLETE_USERS,
            fetchPolicy: 'network-only',
        });
        return membersData.data.autocomplete.users.edges.map((user) => user.node);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addChat = createAsyncThunk('chat/addChat', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ url, title, userIds = [] }, { rejectWithValue }) {
    var _c, _d, _e;
    try {
        const addChatData = yield client.mutate({
            mutation: ADD_CONVERSATION,
            variables: { url, title, userIds },
            context: { silentReadOnly: true },
        });
        return addChatData.data.addConversation.conversation;
    }
    catch (error) {
        if (isReadOnlyError(error)) {
            const chatData = yield client.query({
                query: GET_CONVERSATION,
                variables: { url },
                fetchPolicy: 'network-only',
            });
            return (_e = (_d = (_c = chatData.data) === null || _c === void 0 ? void 0 : _c.me) === null || _d === void 0 ? void 0 : _d.conversation) !== null && _e !== void 0 ? _e : null;
        }
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addChatMessage = createAsyncThunk('chat/addChatMessage', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ conversationId, image, text, mentions, }, { rejectWithValue }) {
    try {
        const wrappedMutation = withAttachments((options) => client.mutate(Object.assign({ mutation: ADD_CONVERSATION_MESSAGE }, options)));
        const response = yield wrappedMutation({
            variables: {
                conversationId,
                text: text || '',
                image: image || null,
                mentions: mentions !== null && mentions !== void 0 ? mentions : null,
            },
        });
        return response.data.sendMessage.message;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addChatMember = createAsyncThunk('chat/addChatMember', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ conversationId, userId }, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: ADD_CONVERSATION_MEMBER,
            variables: {
                conversationId,
                userId,
            },
        });
        return response.data.addConversationMember.conversationmember;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeChatMember = createAsyncThunk('chat/removeChatMember', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ conversationId, userId }, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: REMOVE_CONVERSATION_MEMBER,
            variables: { conversationId, userId },
        });
        return Object.assign({ conversationId,
            userId }, response.data.removeConversationMember);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        resetChatState(state) {
            state.id = null;
            state.title = null;
            state.messages = [];
            state.followers = [];
            state.status.fetch = 'idle';
            state.status.addChat = 'idle';
            state.errors.fetch = null;
            state.errors.addChat = null;
            state.refetchFlags.fetch = false;
            state.refetchFlags.addChat = false;
        },
        setChatRefetchFlag(state, action) {
            state.refetchFlags.fetch = action.payload;
        },
        openChat(state) {
            state.opened = true;
        },
        closeChat(state) {
            state.opened = false;
        },
        toggleChat(state) {
            state.opened = !state.opened;
        },
        openScreenshot(state) {
            state.screenshotOpened = true;
        },
        closeScreenshot(state) {
            state.screenshotOpened = false;
        },
        toggleScreenshot(state) {
            state.screenshotOpened = !state.opened;
        },
        addWebSocketMessage: (state, action) => {
            // Add message received via WebSocket without making HTTP request
            if (state.messages) {
                const message = Object.assign(Object.assign({}, action.payload), { sendByMe: action.payload.sendByMe || false });
                const alreadyExists = state.messages.some((existing) => existing.id === message.id);
                if (!alreadyExists) {
                    state.messages = [...state.messages, message];
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchChat
            .addCase(fetchChat.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchChat.fulfilled, (state, action) => {
            var _a, _b, _c;
            state.status.fetch = 'succeeded';
            state.id = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.id;
            state.messages = (_b = action.payload) === null || _b === void 0 ? void 0 : _b.messages.edges.map((message) => message.node);
            state.followers = (_c = action.payload) === null || _c === void 0 ? void 0 : _c.members.edges.map((member) => member.node);
        })
            .addCase(fetchChat.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch =
                action.payload || 'Failed to remove specification projection';
        })
            .addCase(fetchMembers.pending, (state) => {
            state.status.fetchMembers = 'loading';
            state.refetchFlags.fetchMembers = false;
        })
            .addCase(fetchMembers.fulfilled, (state, action) => {
            state.status.fetchMembers = 'succeeded';
            state.members = action.payload;
        })
            .addCase(fetchMembers.rejected, (state, action) => {
            state.status.fetchMembers = 'failed';
            state.errors.fetchMembers =
                action.payload || 'Failed to remove specification projection';
        })
            .addCase(addChat.pending, (state) => {
            state.status.addChat = 'loading';
            state.refetchFlags.addChat = false;
        })
            .addCase(addChat.fulfilled, (state, action) => {
            state.status.addChat = 'succeeded';
            if (!action.payload)
                return;
            // addChat is effectively an upsert: it resolves with the existing conversation
            // when one is already there. Only reset messages/followers when it actually
            // switched to a different conversation - otherwise this can race with fetchChat
            // (dispatched alongside it) and wipe out messages that were just loaded.
            const isNewConversation = state.id !== action.payload.id;
            state.id = action.payload.id;
            state.title = action.payload.title;
            if (isNewConversation) {
                state.messages = [];
                state.followers = [];
            }
        })
            .addCase(addChat.rejected, (state, action) => {
            state.status.addChat = 'failed';
            state.errors.addChat =
                action.payload || 'Failed to remove specification projection';
        })
            .addCase(addChatMessage.pending, (state) => {
            state.status.addMessage = 'loading';
        })
            .addCase(addChatMessage.fulfilled, (state, action) => {
            state.status.addMessage = 'succeeded';
            const alreadyExists = state.messages.some((existing) => existing.id === action.payload.id);
            if (!alreadyExists) {
                state.messages = [...state.messages, Object.assign(Object.assign({}, action.payload), { sendByMe: true })];
            }
        })
            .addCase(addChatMessage.rejected, (state, action) => {
            state.status.addMessage = 'failed';
            state.errors.addMessage =
                action.payload || 'Failed to remove specification projection';
        })
            .addCase(addChatMember.pending, (state) => {
            state.status.addMember = 'loading';
        })
            .addCase(addChatMember.fulfilled, (state, action) => {
            state.status.addMember = 'succeeded';
            state.followers = [...(state.followers || []), action.payload];
        })
            .addCase(addChatMember.rejected, (state, action) => {
            state.status.addMember = 'failed';
            state.errors.addMember =
                action.payload || 'Failed to remove specification projection';
        })
            .addCase(removeChatMember.pending, (state) => {
            state.status.removeMember = 'loading';
        })
            .addCase(removeChatMember.fulfilled, (state, action) => {
            state.status.removeMember = 'succeeded';
            if (action.payload.removed) {
                // Compare the nested user id, not the conversationmember's own id —
                // otherwise this never matches and the follower never actually
                // disappears locally, despite the backend call succeeding.
                state.followers = (state.followers || []).filter((member) => { var _a; return ((_a = member.user) === null || _a === void 0 ? void 0 : _a.id) !== action.meta.arg.userId; });
            }
        })
            .addCase(removeChatMember.rejected, (state, action) => {
            state.status.removeMember = 'failed';
            state.errors.removeMember =
                action.payload || 'Failed to remove specification projection';
        });
    },
});
export const { resetChatState, setChatRefetchFlag, openChat, closeChat, toggleChat, openScreenshot, closeScreenshot, toggleScreenshot, addWebSocketMessage, } = chatSlice.actions;
export const selectChatStatus = (state) => state.chat.status;
export const selectChatErrors = (state) => state.chat.errors;
export const selectChatRefetchFlags = (state) => state.chat.refetchFlags;
export const selectChat = (state) => state.chat;
export const selectChatMessages = (state) => state.chat.messages;
export const selectChatFollowers = (state) => state.chat.followers;
export const selectChatMembers = (state) => state.chat.members;
export default chatSlice.reducer;
